import { create } from 'zustand'

import { fetchWebOfficialPublicConfigApi } from '@/network/api/config'
import type { WebOfficialPublicConfig } from '@/network/api/config'

/**
 * 前端运行时公开配置（配置中心 L2）
 * 应用启动时从 apigate 拉取 scope=PUBLIC 的配置；失败时使用硬编码兜底值。
 * OSS 地址由配置中心 yun3d_oss.base_url 下发，前端不硬编码在 .env 中。
 */

// 默认值：配置中心不可用时的兜底
const envDefaults: WebOfficialPublicConfig = {
  siteName: '大宇3D打印',
  siteLogo: '/logo.png',
  supportEmail: '',
  supportPhone: '',
  miniProgramQrcodeUrl: '',
  wechatQrcodeUrl: '',
}

interface AppConfigState {
  loaded: boolean
  loading: boolean
  config: WebOfficialPublicConfig
  load: () => Promise<void>
}

// 模块级变量：保存 in-flight promise，避免竞态条件
let loadPromise: Promise<void> | null = null

export const useAppConfigStore = create<AppConfigState>((set, get) => ({
  loaded: false,
  loading: false,
  config: { ...envDefaults },
  load: async () => {
    // 已完成加载，直接返回
    if (get().loaded) return
    // 已有 in-flight 请求，等待它完成（解决 main.tsx 与 getOfficialData 竞态）
    if (loadPromise) return loadPromise

    loadPromise = (async () => {
      set({ loading: true })
      try {
        const res = await fetchWebOfficialPublicConfigApi()
        const remote = res?.data?.config
        if (remote && typeof remote === 'object') {
          // 仅覆盖远程非空字段，空值回退到 env 默认
          const merged: WebOfficialPublicConfig = { ...envDefaults }
          for (const [key, value] of Object.entries(remote)) {
            if (value !== null && value !== undefined && value !== '') {
              merged[key] = value
            }
          }
          set({ loaded: true, config: merged })
          return
        }
        set({ loaded: true })
      } catch {
        // 配置中心不可用：静默回退到 env 默认值
        set({ loaded: true })
      } finally {
        set({ loading: false })
        loadPromise = null
      }
    })()

    return loadPromise
  },
}))

/**
 * 读取配置项（非响应式，适合工具函数 / 事件回调中使用）
 * @param key 配置键（camelCase，如 imgBaseUrl）
 * @param fallback 远程与 env 均无值时的兜底
 */
export function getAppConfig<T = string>(key: keyof WebOfficialPublicConfig | string, fallback?: T): T {
  const value = useAppConfigStore.getState().config[key as string]
  if (value === null || value === undefined || value === '') {
    return fallback as T
  }
  return value as T
}
